import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { SWRConfig } from "swr"

import AppLayout from "layouts/app-layout"
import KpiCard from "components/dashboard/kpi-card"
import SectionCard from "components/dashboard/section-card"
import SalesTrendChart from "components/dashboard/sales-trend-chart"

import { useAuth } from "contexts/auth-context"

import useSalesDateSummaryReportRepositories from "repositories/sales-date-summary-report"
import useTopTenSoldItemsReportRepositories from "repositories/top-ten-sold-items-report"
import useStockBelowMinimumRepositories from "repositories/stock-below-minimum"
import useCustomerDebtReportRepositories from "repositories/customer-debt-report"
import useMasterStockValueGlobalReportRepositories from "repositories/master-stock-value-global-report"
import useDailySalesSummaryRepositories from "repositories/daily-sales-summary"
import { getDefaultOutletFilterValue, resolveOutletFilter, getOutletFilterOptions } from "helpers/general-helper"
import OutletFilterSelect from "components/forms/outlet-filter-select"

const toInputDate = (date) => {
    const d = date instanceof Date ? date : new Date(date)
    if (Number.isNaN(d.getTime())) return ""
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${y}-${m}-${day}`
}

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID")

const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(value ?? 0))

// Penjualan harian memakai endpoint /orders/daily-sales-summary yang memakai
// logika IDENTIK laporan EOD (paid_date + status PAID/DONE/SHIPPING/CONFIRMED),
// supaya angka dashboard cocok dengan EOD. Agregasi & filter tanggal dilakukan
// di SQL (bukan client-side), jadi tidak ada batas paginasi.
const useOrdersAggregate = ({ dateFrom, dateTo, outletId }) => {
    const { data, error, isValidating } = useDailySalesSummaryRepositories({
        date_from: dateFrom,
        date_to: dateTo,
        outlet_id: outletId || undefined,
        enabled: Boolean(dateFrom) && Boolean(dateTo),
    })

    const aggregate = useMemo(() => ({
        summary: {
            ttl_penjualan: Number(data?.summary?.ttl_penjualan ?? 0),
            hutang: Number(data?.summary?.hutang ?? 0),
            count: Number(data?.summary?.count ?? 0),
        },
        byDate: Array.isArray(data?.byDate) ? data.byDate : [],
    }), [data])

    return {
        data: aggregate,
        isLoading: !error && !data,
        error,
        isValidating,
    }
}

// ---------- OWNER SECTION ----------

const OwnerSection = ({ outletId, monthsBackLimit = null }) => {
    const today = useMemo(() => new Date(), [])
    const startOfMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today])
    const sevenDaysAgo = useMemo(() => new Date(today.getTime() - 6 * 86400000), [today])
    // ATALLA-387: admin toko (non-super) ber-permission penjualan hanya boleh melihat
    // transaksi maksimal `monthsBackLimit` bulan ke belakang — rentang "tahun berjalan"
    // di-clamp; super user (monthsBackLimit null) tetap dari awal tahun.
    const startOfYear = useMemo(() => {
        const start = new Date(today.getFullYear(), 0, 1)
        if (!monthsBackLimit) return start
        const clamp = new Date(today.getFullYear(), today.getMonth() - monthsBackLimit, today.getDate())
        return clamp > start ? clamp : start
    }, [today, monthsBackLimit])

    const todayStr = toInputDate(today)
    const monthStartStr = toInputDate(startOfMonth)
    const sevenStr = toInputDate(sevenDaysAgo)
    const yearStartStr = toInputDate(startOfYear)

    const { data: todaySales, isLoading: loadingToday } = useOrdersAggregate({
        dateFrom: todayStr,
        dateTo: todayStr,
        outletId,
    })

    const { data: mtdSales, isLoading: loadingMtd } = useSalesDateSummaryReportRepositories({
        date_from: monthStartStr,
        date_to: todayStr,
        outlet_id: outletId || undefined,
        enabled: true,
    })

    const { data: ytdSales, isLoading: loadingYtd } = useSalesDateSummaryReportRepositories({
        date_from: yearStartStr,
        date_to: todayStr,
        outlet_id: outletId || undefined,
        enabled: true,
    })

    const { data: weekSales, isLoading: loadingWeek } = useOrdersAggregate({
        dateFrom: sevenStr,
        dateTo: todayStr,
        outletId,
    })

    const { data: debt, isLoading: loadingDebt } = useCustomerDebtReportRepositories({
        date_from: yearStartStr,
        date_to: todayStr,
        outlet_id: outletId || undefined,
        enabled: true,
    })

    const { data: stockValue, isLoading: loadingStockValue } = useMasterStockValueGlobalReportRepositories({
        date_from: todayStr,
        date_to: todayStr,
        outlet_id: outletId || undefined,
        enabled: true,
    })

    const { data: topItems, isLoading: loadingTop } = useTopTenSoldItemsReportRepositories({
        date_from: sevenStr,
        date_to: todayStr,
        outlet_id: outletId || undefined,
        enabled: true,
    })

    const chartData = useMemo(() => {
        const rows = weekSales?.byDate ?? []
        const labelFormatter = new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "short" })
        return rows.map((row) => {
            const d = row.date ? new Date(`${row.date}T00:00:00`) : null
            const label = d && !Number.isNaN(d.getTime()) ? labelFormatter.format(d) : row.date
            return { label, value: Number(row.ttl_penjualan ?? 0) }
        })
    }, [weekSales])

    const topRows = useMemo(() => (topItems?.rows ?? []).slice(0, 10), [topItems])

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-base font-semibold text-neutral-900">Ringkasan Finansial</h2>
                <p className="mt-0.5 text-xs text-neutral-500">Performa penjualan & posisi finansial</p>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
                <KpiCard
                    label="Penjualan Hari Ini"
                    value={todaySales?.summary?.ttl_penjualan}
                    sublabel={todayStr}
                    format="currency"
                    isLoading={loadingToday}
                />
                <KpiCard
                    label="Penjualan Bulan Ini"
                    value={mtdSales?.summary?.ttl_penjualan}
                    sublabel="Bulan berjalan"
                    format="currency"
                    isLoading={loadingMtd}
                />
                <KpiCard
                    label={monthsBackLimit ? `Penjualan ${monthsBackLimit} Bulan` : "Penjualan Tahun Ini"}
                    value={ytdSales?.summary?.ttl_penjualan}
                    sublabel={monthsBackLimit ? `sejak ${yearStartStr}` : "Tahun berjalan"}
                    format="currency"
                    isLoading={loadingYtd}
                />
                <KpiCard
                    label="Piutang Customer"
                    value={debt?.summary?.total_saldo_akhir}
                    sublabel={`${formatNumber(debt?.summary?.total_customers)} customer`}
                    accent="warning"
                    format="currency"
                    isLoading={loadingDebt}
                />
                <KpiCard
                    label="Nilai Inventory"
                    value={stockValue?.summary?.grand_total ?? stockValue?.grand_total?.total_value ?? stockValue?.total_value}
                    sublabel={`per ${todayStr}`}
                    format="currency"
                    isLoading={loadingStockValue}
                />
            </div>

            <SectionCard
                title="Tren Penjualan 7 Hari Terakhir"
                subtitle={`${sevenStr} → ${todayStr}`}
                isLoading={loadingWeek}
                isEmpty={!loadingWeek && chartData.length === 0}
                emptyText="Tidak ada penjualan dalam 7 hari terakhir"
                action={
                    <Link
                        to="/reports/sales-date-summary"
                        className="text-xs font-medium text-neutral-700 hover:text-neutral-900"
                    >
                        Lihat detail →
                    </Link>
                }
            >
                <SalesTrendChart data={chartData} />
            </SectionCard>

            <SectionCard
                title="Top 10 Barang Laku (7 Hari)"
                subtitle="Berdasarkan frekuensi order & total qty keluar"
                isLoading={loadingTop}
                isEmpty={!loadingTop && topRows.length === 0}
                action={
                    <Link
                        to="/reports/top-ten-sold-items"
                        className="text-xs font-medium text-neutral-700 hover:text-neutral-900"
                    >
                        Lihat detail →
                    </Link>
                }
            >
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="text-xs text-neutral-500">
                                <th className="py-2 font-semibold text-left">KATEGORI</th>
                                <th className="py-2 font-semibold text-left">GROUP BARANG</th>
                                <th className="py-2 font-semibold text-right">FREK</th>
                                <th className="py-2 font-semibold text-right">KELUAR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topRows.map((row, index) => {
                                const prevCategory = index > 0 ? topRows[index - 1]?.category : null
                                const showCategory = row.category !== prevCategory
                                return (
                                    <tr key={row.row_key || `${row.category}-${row.group_barang}-${index}`} className="border-t border-neutral-100 hover:bg-brand-50 transition-colors">
                                        <td className="py-2 pr-3 text-neutral-700">{showCategory ? row.category : ""}</td>
                                        <td className="py-2 pr-3 text-neutral-800">{row.group_barang}</td>
                                        <td className="py-2 pr-3 text-right text-neutral-700">{formatNumber(row.freq)}</td>
                                        <td className="py-2 font-semibold text-right text-neutral-900">{formatNumber(row.keluar)}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </SectionCard>
        </div>
    )
}

// ---------- SUPERVISOR SECTION ----------

const SupervisorSection = ({ outletId }) => {
    const today = useMemo(() => new Date(), [])
    const todayStr = toInputDate(today)
    const sevenStr = toInputDate(new Date(today.getTime() - 6 * 86400000))

    const { data: todaySales, isLoading: loadingToday } = useOrdersAggregate({
        dateFrom: todayStr,
        dateTo: todayStr,
        outletId,
    })

    const { data: stockAlert, isLoading: loadingStock } = useStockBelowMinimumRepositories({
        outlet_id: outletId || undefined,
        batas_bulan: "3",
        product_model_from: "",
        product_model_to: "",
        search: "",
        enabled: Boolean(outletId),
    })

    const { data: topWeek, isLoading: loadingTop } = useTopTenSoldItemsReportRepositories({
        date_from: sevenStr,
        date_to: todayStr,
        outlet_id: outletId || undefined,
        enabled: true,
    })

    const stockBelowItems = useMemo(() => {
        const groups = stockAlert?.groups ?? []
        return groups.flatMap((g) =>
            (g.items ?? []).map((it) => ({
                group: g.group_name,
                name: it.product_unit_name,
                stock: it.stock,
                rata_rata: it.rata_rata,
                po: it.po,
            }))
        ).slice(0, 10)
    }, [stockAlert])

    const topRows = useMemo(() => (topWeek?.rows ?? []).slice(0, 5), [topWeek])

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-base font-semibold text-neutral-900">Operasional Harian</h2>
                <p className="mt-0.5 text-xs text-neutral-500">Aktivitas & alert yang perlu tindakan</p>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <KpiCard
                    label="Penjualan Hari Ini"
                    value={todaySales?.summary?.ttl_penjualan}
                    sublabel={todayStr}
                    format="currency"
                    isLoading={loadingToday}
                />
                <KpiCard
                    label="Hutang Hari Ini"
                    value={todaySales?.summary?.hutang}
                    sublabel="Belum dibayar"
                    accent="warning"
                    format="currency"
                    isLoading={loadingToday}
                />
                <KpiCard
                    label="Transaksi Hari Ini"
                    value={todaySales?.summary?.count}
                    sublabel="Jumlah order masuk"
                    isLoading={loadingToday}
                />
                <KpiCard
                    label="Item Stock Menipis"
                    value={stockAlert?.grand_total?.total_items}
                    sublabel={`${formatNumber(stockAlert?.grand_total?.total_groups)} group`}
                    accent="negative"
                    isLoading={loadingStock}
                />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <SectionCard
                    title="Stock di Bawah Minimum"
                    subtitle="Item yang perlu segera di-reorder"
                    isLoading={loadingStock}
                    isEmpty={!loadingStock && stockBelowItems.length === 0}
                    emptyText={!outletId ? "Pilih outlet untuk melihat alert stock" : "Tidak ada item di bawah minimum"}
                    action={
                        <Link
                            to="/reports/stock-below-minimum"
                            className="text-xs font-medium text-neutral-700 hover:text-neutral-900"
                        >
                            Lihat semua →
                        </Link>
                    }
                >
                    <ul className="divide-y divide-neutral-100">
                        {stockBelowItems.map((it, idx) => (
                            <li key={`${it.group}-${it.name}-${idx}`} className="flex items-start justify-between gap-3 py-2.5">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate text-neutral-900">{it.name}</div>
                                    <div className="text-xs text-neutral-500">{it.group}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-semibold text-rose-600">{formatNumber(it.stock)}</div>
                                    <div className="text-xs text-neutral-400">avg {formatNumber(it.rata_rata)}/bln</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </SectionCard>

                <SectionCard
                    title="Top 5 Barang Laku (7 Hari)"
                    subtitle="Yang paling sering keluar"
                    isLoading={loadingTop}
                    isEmpty={!loadingTop && topRows.length === 0}
                    action={
                        <Link
                            to="/reports/top-ten-sold-items"
                            className="text-xs font-medium text-neutral-700 hover:text-neutral-900"
                        >
                            Lihat semua →
                        </Link>
                    }
                >
                    <ul className="divide-y divide-neutral-100">
                        {topRows.map((row, idx) => (
                            <li key={row.row_key || `${row.category}-${row.group_barang}-${idx}`} className="flex items-center justify-between gap-3 py-2.5">
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="flex items-center justify-center w-7 h-7 text-xs font-bold rounded-full bg-neutral-100 text-neutral-700">
                                        {idx + 1}
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate text-neutral-900">{row.group_barang}</div>
                                        <div className="text-xs text-neutral-500">{row.category}</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-semibold text-neutral-900">{formatNumber(row.keluar)}</div>
                                    <div className="text-xs text-neutral-400">{formatNumber(row.freq)} freq</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </SectionCard>
            </div>

            <SectionCard
                title="Aksi Cepat"
                subtitle="Akses cepat fitur yang sering dipakai"
            >
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <Link to="/goods-receipt" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Goods Receipt
                    </Link>
                    <Link to="/stock-adjustment" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Koreksi Stok
                    </Link>
                    {/* 270726 (permintaan user): shortcut ke dua menu yang sering dibuka. */}
                    <Link to="/reports/sales-qty-yearly" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Qty Penjualan Setahun
                    </Link>
                    <Link to="/product/stock" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Product Stocks
                    </Link>
                </div>
            </SectionCard>
        </div>
    )
}

// ---------- ADMIN SECTION ----------

const AdminSection = () => {
    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-base font-semibold text-neutral-900">Administrasi Sistem</h2>
                <p className="mt-0.5 text-xs text-neutral-500">Konfigurasi & manajemen data master</p>
            </div>

            <SectionCard
                title="Tautan Cepat"
                subtitle="Akses cepat ke pengaturan & data master"
            >
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                    <Link to="/settings" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Pengaturan
                    </Link>
                    <Link to="/product/model" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Produk
                    </Link>
                    <Link to="/brand" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Brand
                    </Link>
                    <Link to="/product/series" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Series
                    </Link>
                    <Link to="/satuan" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Satuan
                    </Link>
                    <Link to="/discount" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Discount
                    </Link>
                    <Link to="/reward" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Reward
                    </Link>
                    <Link to="/customer-notes" className="px-4 py-3 text-sm font-medium text-center transition border rounded-xl border-neutral-200 hover:border-brand-900 hover:bg-neutral-50">
                        Customer Notes
                    </Link>
                </div>
            </SectionCard>
        </div>
    )
}

// ---------- ROOT ----------

// Near-realtime: semua hook SWR di dalam dashboard ikut auto-refresh pakai config ini.
// refreshInterval = jeda polling (ms). Set 0 untuk matikan.
const REALTIME_SWR_CONFIG = {
    refreshInterval: 30000,        // tarik ulang tiap 30 detik
    revalidateOnFocus: true,       // refresh saat tab kembali fokus
    revalidateOnReconnect: true,   // refresh saat koneksi pulih
    keepPreviousData: true,        // tahan data lama biar UI gak flicker saat refetch
    dedupingInterval: 5000,
}

const Dashboard = () => {
    const { user, currentBranch, isSuper, can, canFilterAllOutlets } = useAuth()
    const isSuperUser = isSuper()
    // ATALLA-403: super ATAU pemegang node 'outlet.filter-entity' (admin HO) boleh
    // memilih outlet — daftar outlet sudah dibatasi ke entitasnya oleh repo branch.
    const canPickOutlet = canFilterAllOutlets()
    // Permission menu dashboard (node: dashboard.view, seeder AtallaCmsMenuPermissionSeeder).
    // Sidebar menyembunyikan link-nya, tapi "/" tetap route index — guard di sini supaya
    // user tanpa permission tidak melihat KPI/omzet saat mendarat di "/".
    const canViewDashboard = can('dashboard.view')

    // Nilai filter outlet: sentinel grup (GRP_ALL/GRP_HO) atau id outlet tunggal.
    // Default "ikut outlet user"; OutletFilterSelect membaca daftar outlet dari sesi.
    const [selectedOutlet, setSelectedOutlet] = useState(() => getDefaultOutletFilterValue())

    // Grup/outlet → CSV id yang dikirim ke semua endpoint (outlet_id).
    const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(selectedOutlet)
    const outletId = resolvedOutletCsv
    const selectedOutletName = useMemo(() => {
        const opt = getOutletFilterOptions().find((o) => String(o.value) === String(selectedOutlet))
        return opt?.label || currentBranch?.name || "Semua Outlet"
    }, [selectedOutlet, currentBranch])

    const greeting = useMemo(() => {
        const hour = new Date().getHours()
        if (hour < 11) return "Selamat pagi"
        if (hour < 15) return "Selamat siang"
        if (hour < 19) return "Selamat sore"
        return "Selamat malam"
    }, [])

    if (!canViewDashboard) {
        return (
            <div className="space-y-2">
                <h1 className="text-2xl font-semibold text-neutral-900">
                    {greeting}, {user?.name || "Pengguna"} 👋
                </h1>
                <p className="text-sm text-neutral-500">
                    Anda tidak memiliki akses untuk melihat dashboard. Silakan gunakan menu di samping, atau hubungi admin untuk meminta akses.
                </p>
            </div>
        )
    }

    return (
        <SWRConfig value={REALTIME_SWR_CONFIG}>
        <div className="space-y-8">
            <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-neutral-900">
                        {greeting}, {user?.name || "Pengguna"} 👋
                    </h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        {`Outlet: ${selectedOutletName}`}
                        {isSuperUser ? " • Super User" : ""}
                    </p>
                </div>
                <div className="flex flex-col items-start gap-2 md:items-end">
                    {canPickOutlet ? (
                        <label className="flex items-center gap-2 text-xs text-neutral-500">
                            <span className="font-medium text-neutral-700">Filter Outlet</span>
                            <OutletFilterSelect
                                value={selectedOutlet}
                                onChange={(v) => setSelectedOutlet(v)}
                                className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-900 shadow-sm focus:border-brand-900 focus:outline-none focus:ring-1 focus:ring-brand-900"
                                title="Outlet"
                            />
                        </label>
                    ) : (
                        <span className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm font-medium text-neutral-700">
                            {selectedOutletName}
                        </span>
                    )}
                    <div className="text-xs text-neutral-400">
                        {new Date().toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                    </div>
                </div>
            </div>

            {isSuperUser ? (
                <>
                    <OwnerSection outletId={outletId} />
                    <SupervisorSection outletId={outletId} />
                    <AdminSection />
                </>
            ) : (
                <>
                    {/* ATALLA-387: admin toko ber-permission penjualan dashboard melihat
                        ringkasan finansial, dibatasi 3 bulan ke belakang.
                        ATALLA-431: admin HO (pemegang node 'outlet.filter-entity' →
                        canPickOutlet) melihat penjualan tahun berjalan (year-to-date, sama
                        seperti super user), bukan dibatasi 3 bulan seperti admin toko. */}
                    {can('dashboard.sales.view') && (
                        <OwnerSection outletId={outletId} monthsBackLimit={canPickOutlet ? null : 3} />
                    )}
                    <SupervisorSection outletId={outletId} />
                </>
            )}
        </div>
        </SWRConfig>
    )
}

export default Dashboard
